import { getMultiCartItems } from 'api/cart/getMultiCartItems'
import { getMultiCartSummaries } from 'api/cart/getMultiCartSummaries'
import { getMultiCartMembers } from 'api/cart/getMultiCartMembers'
import { assertEvent, assign, DoneActorEvent, ErrorActorEvent, fromPromise, log, setup } from 'xstate'
import { inviteUsersToMultiCart, MultiCartInvitationUsers } from 'api/cart/inviteUsersToMultiCart'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { createMultiCart } from 'api/cart/createMultiCart'
import { addItemsToMultiCart } from 'api/cart/addItemsToMultiCart'
import { History } from 'history'
import { joinUserToMultiCart } from 'api/cart/joinUserToMultiCart'
import { buildUrl } from 'lib/url/searchUrlUtils'
import { deleteMultiCart } from 'api/cart/deleteMultiCart'
import { updateMultiCartName } from 'api/cart/updateMultiCartName'
import { leaveMultiCart } from 'api/cart/leaveMultiCart'
import { revokeUserAccessMultiCart } from 'api/cart/revokeUserAccessMultiCart'
import { get as getLocalStorage, set as setLocalStorage } from 'lib/storage/isomorphicLocalStorage'
import { moveItemsToTargetCart } from 'api/cart/moveItemsToTargetCart'
import { createAndMoveItemsToTargetCart } from 'api/cart/createAndMoveItemsToTargetCart'
import { updateItemsInMultiCart } from 'api/cart/updateItemsInMultiCart'
import { deleteItemsFromMultiCart } from 'api/cart/deleteItemsFromMultiCart'

const ACTIVE_CART_ID_STORAGE_KEY = 'sharedCarts.activeCartId'

export type MultiCartMachineInput = {
  memberId?: string
  history: History<unknown>
  currentRegionCode: string
  sharedCartsFeatureFlag: boolean
}

export type CartModalConfig = {
  multiCartItemIdsToMove: Array<string>
}

export const CART_MODAL_EVENT_NAME = 'multiCartMachineModal'

type MultiCartMachineContext = {
  cartSummary: { status: 'loaded', data: MachineContext.CartSummary } | { status: 'error', error: string } | { status: 'loading' }
  cartItems: { status: 'loaded', data: MachineContext.CartItems } | { status: 'error', error: string } | { status: 'loading' }
  cartMembers: { status: 'loaded', data: MachineContext.CartMembers } | { status: 'error', error: string } | { status: 'loading' }
  activeCartId?: string
  // Redux state from input
  memberId?: string
  history: History<unknown>
  currentRegionCode: string
  sharedCartsFeatureFlag: boolean
  creatingNewCart: boolean
}

type InviteMultiCartUsers = {
  type: 'invite multi cart users'
  cartId: string
  users: MultiCartInvitationUsers
}

type CreateMultiCart = {
  type: 'create multi cart'
  cartName: string
  items: Array<App.Checkout.AnyItem>
}

type MoveItemsToMultiCart = {
  type: 'select and move items to target cart'
  multiCartItemIdsToMove: Array<string>
  targetCartId: string
}

type DeleteMultiCart = {
  type: 'delete multi cart'
  cartId: string
}

type UpdateMultiCart = {
  type: 'update multi cart'
  cartId: string
  name: string
}

type LeaveMultiCart = {
  type: 'leave multi cart'
  cartId: string
}

type RevokeUserAccess = {
  type: 'revoke user access'
  cartId: string
  userId: string
}

type SetActiveCart = {
  type: 'set active cart'
  cartId: string
}

type CreateAndMoveItemsToTargetCart = {
  type: 'create and move items to target cart'
  cartName: string
  multiCartItemIdsToMove: Array<string>
}

type UpdateCartItem = {
  type: 'update cart item'
  cartId: string
  multiCartItem: App.MultiCartItem
}

type MultiCartMachineEvents = {
  type: 'successful login'
} | {
  type: 'failed login'
} | {
  type: 'join multi cart'
  sharedCartId: string
} | {
  type: 'add items to active cart'
  items: Array<App.Checkout.AnyItem>
  skipNotification: boolean
} | {
  type: 'delete all items from active cart'
} | InviteMultiCartUsers | CreateMultiCart | MoveItemsToMultiCart | DeleteMultiCart | CreateAndMoveItemsToTargetCart
| UpdateMultiCart | LeaveMultiCart | RevokeUserAccess | SetActiveCart | UpdateCartItem
| DoneActorEvent<App.MultiCartSummary, string>
| DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>
| DoneActorEvent<MachineContext.CartSummary, string>
| DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>
| DoneActorEvent<Array<MachineContext.CartItems>, string>
| DoneActorEvent<Array<MachineContext.CartMembers>, string>
| ErrorActorEvent<MachineContext.CartSummary, string>
| ErrorActorEvent<Array<MachineContext.CartItems>, string>
| ErrorActorEvent<Array<MachineContext.CartMembers>, string>

/**
 * State machine docs - https://stately.ai/docs/state-machines-and-statecharts
 */

const multiCartMachine = setup({
  types: {
    context: {} as MultiCartMachineContext,
    events: {} as MultiCartMachineEvents,
    input: {} as MultiCartMachineInput,
  },
  actors: {
    createMultiCart: fromPromise(({ input }: { input: { cartName: string, items: Array<App.Checkout.AnyItem> } }) => createMultiCart(input.cartName, input.items)),
    addItemsToMultiCart: fromPromise(({ input }: { input: { cartId: string, items: Array<App.Checkout.AnyItem>, skipNotification: boolean } }) => addItemsToMultiCart(input.cartId, input.items, input.skipNotification)),
    updateMultiCartName: fromPromise(({ input }: { input: { cartId: string, name: string } }) => updateMultiCartName(input.cartId, input.name)),
    deleteMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => deleteMultiCart(input.cartId)),
    leaveMultiCart: fromPromise(({ input }: { input: { cartId: string } }) => leaveMultiCart(input.cartId)),
    getMultiCarts: fromPromise(getMultiCartSummaries),
    getMultiCartItems: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartItems))),
    getMultiCartMembers: fromPromise(({ input }: { input: Array<string> }) => Promise.all(input.map(getMultiCartMembers))),
    inviteUsersToMultiCart: fromPromise(({ input }: { input: { cartId: string, users: MultiCartInvitationUsers } }) => inviteUsersToMultiCart(input.cartId, input.users)),
    joinUserToMultiCart: fromPromise(({ input }: { input: { sharedCartId: string } }) => joinUserToMultiCart(input.sharedCartId)),
    revokeUserAccessMultiCart: fromPromise(({ input }: { input: { cartId: string, userId: string } }) => revokeUserAccessMultiCart(input.cartId, input.userId)),
    moveItemsToTargetCart: fromPromise(({ input }: { input: { sourceCartId: string, targetCartId: string, itemIdsToMove: Array<string> } }) => moveItemsToTargetCart(input)),
    createAndMoveItemsToTargetCart: fromPromise(({ input }: { input: { sourceCartId: string, cartName: string, itemIdsToMove: Array<string> } }) => createAndMoveItemsToTargetCart(input)),
    // updateItemsInMultiCart is not yet used, but use it when we need to update items in a multi cart
    updateItemsInMultiCart: fromPromise(({ input }: { input: { cartId: string, items: Array<App.Checkout.AnyItem> } }) => updateItemsInMultiCart(input.cartId, input.items)),
    deleteItemsInMultiCart: fromPromise(({ input }: { input: { cartId: string, itemIdsToDelete: Array<string> } }) => deleteItemsFromMultiCart(input.cartId, input.itemIdsToDelete)),
  },
  actions: {
    assignCartSummary: assign({
      cartSummary: ({ event }: { event: DoneActorEvent<MachineContext.CartSummary, string> }) => ({
        status: 'loaded' as const,
        data: event.output,
      })
    }),
    assignCartSummaryFetchError: assign({
      cartSummary: () => ({
        status: 'error' as const,
        error: 'Failed to load cart summary'
      })
    }),
    assignCartItems: assign({
      cartItems: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string> }) => {
        // Merge all cart items from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)
        }
      }
    }),
    emptyCartItemsOfActiveCart: assign({
      cartItems: ({ context }: { context: MultiCartMachineContext }) => {
        const { activeCartId } = context
        if (!activeCartId) {
          return context.cartItems
        }

        return {
          ...context.cartItems,
          data: {
            ...(context.cartItems.status === 'loaded' ? context.cartItems.data : {}),
            [activeCartId]: {}
          }
        }
      }
    }),
    appendCartItems: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartItems>, string>, context: MultiCartMachineContext }) => {
        const newCartItems = event.output.reduce((acc, cartItems) => ({ ...acc, ...cartItems }), {} as MachineContext.CartItems)

        return {
          status: 'loaded' as const,
          data: {
            ...context.cartItems.status === 'loaded' ? context.cartItems.data : {},
            ...newCartItems,
          }
        }
      }
    }),
    appendItemsToCart: assign({
      cartItems: ({ event, context }: { event: DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>, context: MultiCartMachineContext }) => {
        const newCartItems = event.output.cartItems
        const existingData = context.cartItems.status === 'loaded' ? context.cartItems.data : {}
        const updatedData = { ...existingData }

        Object.keys(newCartItems).forEach(cartId => {
          updatedData[cartId] = {
            ...existingData[cartId] || {},
            ...newCartItems[cartId],
          }
        })

        return {
          status: 'loaded' as const,
          data: updatedData
        }
      }
    }),
    appendCartMembers: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string>, context: MultiCartMachineContext }) => {
        const newCartMembers = event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.status === 'loaded' ? context.cartMembers.data : {},
            ...newCartMembers,
          }
        }
      }
    }),
    assignCartItemsFetchError: assign({
      cartItems: () => ({
        status: 'error' as const,
        error: 'Failed to load cart items'
      })
    }),
    assignCartMembers: assign({
      cartMembers: ({ event }: { event: DoneActorEvent<Array<MachineContext.CartMembers>, string> }) => {
        // Merge all cart members from Promise.all results into a single object
        return {
          status: 'loaded' as const,
          data: event.output.reduce((acc, cartMembers) => ({ ...acc, ...cartMembers }), {} as MachineContext.CartMembers)
        }
      }
    }),
    assignCartMembersFetchError: assign({
      cartMembers: () => ({
        status: 'error' as const,
        error: 'Failed to load cart members'
      })
    }),
    assignNewlyCreatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: DoneActorEvent<App.MultiCartSummary, string>, context: MultiCartMachineContext }) => {
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartSummary.status === 'loaded' ? context.cartSummary.data : {},
            [event.output.cartId]: event.output,
          }
        }
      }
    }),
    loadActiveCartId: assign({
      activeCartId: ({ context }: { context: MultiCartMachineContext }) => {
        const activeCartIdFromStorage = getLocalStorage(ACTIVE_CART_ID_STORAGE_KEY)

        if (
          context.cartSummary.status === 'loaded' &&
          context.cartSummary.data &&
          activeCartIdFromStorage &&
          context.cartSummary.data[activeCartIdFromStorage]
        ) {
          return activeCartIdFromStorage
        }

        // active card id in storage is not available in the cart summary, set the default cart as active
        if (context.cartSummary.status === 'loaded' && context.cartSummary.data) {
          const data = context.cartSummary.data
          const defaultCart = Object.values(data).find(cart => cart.type === 'default')

          if (defaultCart) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, defaultCart.cartId)
            return defaultCart.cartId
          }
          const cartSummaryValues = Object.values(data)
          if (cartSummaryValues.length > 0 && cartSummaryValues[0]) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, cartSummaryValues[0].cartId)
            return cartSummaryValues[0].cartId
          }
        }

        return context.activeCartId
      },
    }),
    assignActiveCartId: assign({
      activeCartId: ({ context }: { context: MultiCartMachineContext }, params: { cartId: string }) => {
        if (context.activeCartId !== params.cartId) {
          setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, params.cartId)
        }

        return params.cartId
      },
    }),
    setDefaultCartAsActive: assign({
      activeCartId: ({ context }: { context: MultiCartMachineContext }) => {
        if (context.cartSummary.status === 'loaded') {
          const defaultCart = Object.values(context.cartSummary.data).find(cart => cart.type === 'default')

          if (defaultCart && defaultCart.cartId !== context.activeCartId) {
            setLocalStorage(ACTIVE_CART_ID_STORAGE_KEY, defaultCart.cartId)

            return defaultCart.cartId
          }
        }

        return context.activeCartId
      },
    }),
    assignRevokedUserAccess: assign({
      cartMembers: ({ event, context }: { event: DoneActorEvent<{ cartId: string, user: App.MultiCartMember }, string>, context: MultiCartMachineContext }) => {
        if (context.cartMembers.status !== 'loaded' || !event.output.user.userId) {
          return context.cartMembers
        }
        return {
          status: 'loaded' as const,
          data: {
            ...context.cartMembers.data,
            [event.output.cartId]: context.cartMembers.data[event.output.cartId].map(member =>
              member.userId === event.output.user.userId
                ? event.output.user
                : member
            )
          }
        }
      }
    }),
    assignUpdatedCartSummary: assign({
      cartSummary: ({ event, context }: { event: DoneActorEvent<App.MultiCartSummary, string>, context: MultiCartMachineContext }) => {
        if (context.cartSummary.status !== 'loaded') {
          return context.cartSummary
        }

        return {
          ...context.cartSummary,
          data: {
            ...context.cartSummary.data,
            [event.output.cartId]: {
              ...context.cartSummary.data[event.output.cartId],
              name: event.output.name,
            }
          }
        }
      }
    }),
    assignCreatingNewCart: assign({
      creatingNewCart: true
    }),
    unassignCreatingNewCart: assign({
      creatingNewCart: false
    }),
    updateCartItem: assign({
      cartItems: ({ event, context }: { event: UpdateCartItem, context: MultiCartMachineContext }) => {
        if (context.cartItems.status !== 'loaded') {
          return context.cartItems
        }

        const updatedCartItems = { ...context.cartItems.data }

        // Update the specific item in the cart
        if (updatedCartItems[event.cartId]) {
          updatedCartItems[event.cartId] = {
            ...updatedCartItems[event.cartId],
            [event.multiCartItem.id]: event.multiCartItem
          }
        }

        return {
          status: 'loaded' as const,
          data: updatedCartItems
        }
      }
    }),
    // snackbar actions
    showInvitationSuccessSnackbar: () => {
      showSnackbar('Invitations sent!', 'success')
    },
    showInvitationErrorSnackbar: () => {
      showSnackbar('Failed to send invitations', 'critical')
    },
    showJoinCartSuccessSnackbar: () => {
      showSnackbar('You\'ve successfully joined the cart', 'success')
    },
    showJoinCartErrorSnackbar: () => {
      showSnackbar('Failed to join the cart', 'critical')
    },
    showDeleteCartSuccessSnackbar: () => {
      showSnackbar('Cart has been deleted!', 'success')
    },
    showDeleteCartErrorSnackbar: () => {
      showSnackbar('Failed to delete cart', 'critical')
    },
    showUpdateCartSuccessSnackbar: () => {
      showSnackbar('Cart has been updated!', 'success')
    },
    showUpdateCartErrorSnackbar: () => {
      showSnackbar('Failed to update cart', 'critical')
    },
    showLeaveCartSuccessSnackbar: () => {
      showSnackbar('You have left the cart!', 'success')
    },
    showLeaveCartErrorSnackbar: () => {
      showSnackbar('Failed to leave cart', 'critical')
    },
    showRevokeUserAccessSuccessSnackbar: () => {
      showSnackbar('User access has been revoked!', 'success')
    },
    showRevokeUserAccessErrorSnackbar: () => {
      showSnackbar('Failed to revoke user access', 'critical')
    },
    showAddItemsToCartSuccessSnackbar: ({ context, event }) => {
      const newCartItems = (event as DoneActorEvent<{ cartItems: MachineContext.CartItems }, string>).output.cartItems
      const { activeCartId } = context

      if (!activeCartId) {
        return
      }

      const cartName = context.cartSummary.status === 'loaded' && context.cartSummary.data[context.activeCartId!] ? context.cartSummary.data[context.activeCartId!].name : 'cart'

      const items = Object.values(newCartItems[activeCartId])
      const itemIds = items.map(i => i.id)

      showSnackbar(`Item added to ${cartName === 'default' ? 'cart' : cartName}`, 'success', {
        action: {
          label: 'Change cart',
          onAction: () => {
            document.dispatchEvent(new CustomEvent<CartModalConfig>(CART_MODAL_EVENT_NAME, {
              detail: { multiCartItemIdsToMove: itemIds }
            }))
          }
        }
      }) // TODO: CRO-5817 add item name and cart name
    },
    showDeleteAllItemsFromCartSuccessSnackbar: ({ context }) => {
      const cartName = context.cartSummary.status === 'loaded' && context.cartSummary.data[context.activeCartId!] ? context.cartSummary.data[context.activeCartId!].name : 'cart'
      showSnackbar(`All items have been deleted from ${cartName === 'default' ? 'cart' : cartName}`, 'success')
    },
    showDeleteAllItemsFromCartErrorSnackbar: () => {
      showSnackbar('Failed to delete all items from cart', 'critical')
    },
    showMoveItemsToCartSuccessSnackbar: ({ context }) => {
      const cartName = context.cartSummary.status === 'loaded' && context.cartSummary.data[context.activeCartId!] ? context.cartSummary.data[context.activeCartId!].name : 'cart'
      showSnackbar(`Item added to ${cartName === 'default' ? 'cart' : cartName}`, 'success', { action: { label: 'View cart', to: '/cart' } })
    },
    showAddItemsToCartErrorSnackbar: () => {
      showSnackbar('Failed to add items to cart', 'critical')
    },
    showCreateCartSuccessSnackbar: ({ event }) => {
      showSnackbar(`${(event as DoneActorEvent<App.MultiCartSummary, string>).output.name} cart has been created!`, 'success')
    },
    showCreateCartErrorSnackbar: () => {
      showSnackbar('Failed to create cart', 'critical')
    },
    pushToCartPage: ({ context }: { context: MultiCartMachineContext }) => {
      context.history.push(buildUrl('/cart', context.currentRegionCode))
    },
    logErrorEvent: log(({ event }: {
      event: ErrorActorEvent<MachineContext.CartSummary, string> | ErrorActorEvent<Array<MachineContext.CartItems>, string> | ErrorActorEvent<Array<MachineContext.CartMembers>, string>
    }) => `assignCartSummaryFetchError ${JSON.stringify(event.error)}`),
  },
  guards: {
    isUserLoggedIn: ({ context }: { context: MultiCartMachineContext }) => !!context.memberId,
    isSharedCartsFeatureDisabled: ({ context }: { context: MultiCartMachineContext }) => !context.sharedCartsFeatureFlag,
  },
}
).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QFsCuAbALgSwMIEMAnTAWXwGMALbAOzADoqxyBrWqAZU30zAGIA2gAYAuolAAHAPaxsOKTXEgAHogBMQgKz0AnJoDMARgAsx-ZuMA2IWp0AaEAE9EADmP1jOr1aEvDhlzUAdiCAX1CHNCw8IlIKajpGSmY2Gk5uXkFDMSQQaVl5RVzVBE1g+iFLNQNDIJdLQzVLfQdnBEN9YyF6IKEgzR1jAP09Y3DIjBwCYjIqWgYmVnYuHn4BNRzJGTlsBSUStUNtPSNTcx9bVvUhdy8vfRs1P07jNXGQKKnY2YSGWh38OhsAAvdh8CAKBiwDIMT4xGbxeb0f44QEg9jCTZ5baFfaIAJ6ehGNSeTSaIKWSxlTRXdpBJ66YyaPo2QJdFz6d5w6ZxOaJFHYNGgtL0dBSfAQdg8jioZDIIiOcGQ5E0ABuUhYsMm8N5vxVAKBwqgovFkrS0tl8sIjgQtHV5B4uxomMxSnyOz2xXxpnchhuD00zRczPptP8vXoLkCzQCTUC+ksXO1PJ+SIFQvYJolUtiMrlCr4YEIhCkhHoEnQPAAZqXkPRud9EfyaAb0SKxdnzbnLQrbWqpA7Ci7RG6cU68e01BoeqYdNVNB0usYwwnLPQDFH6kYqs03hEPsnG3y-i3UYbMx2zVAeQARMDcbDoWBKxLQ1b1w8I4-6s9t42XnNiDvB8n1dXJ3VxL12gaQx6ACClTC0DRLBcIJaQ0IQdHoaxGh0VD-GeJNohTJsT1bI0syvW973wR9YEowDMAASV4ZB6IAtIXxPdVNQ-Yijz1dNz3bU1GOA2inwYrtiBYsA2KkqA+3tR0FGHLEIPHKD-FeXQUNQjkmgXKwww5WDLD0Flan0epmiIr4v0E09BWE-9ROkzBxLohSeVk+SOKgQti1LctK0wGtCDrBsHLTJyMxEztr1iTzJP8nzWPYtzFLtAcVOdUQwK2ApNNAEptLUXSozqfRDJMSwwzndxLGMPwqi6LQQk5fcot1GLyIvTLqJAjKEp5ABVWAi2Gq8uJVHitX46Lmz6+KqKSmivNS2JxsmhSlJyod8pHcCx09Er8UMSldCnIQtCpYI7vQxogjgoRGgTfwyUOTqJgWnqlt-CjNqA9aUoGraJsIKawSLEsywrataz4+y-rIgH+pGtahu88Gdv8vbBydNTRyK06VHOy65wwwMygpMp0OuioLC6QwdH6Iy7J1VNEkIMAJUVWBUHIcg4FgKsMAAAjFKBaAK7ESaKM7SnKSpqiMOoGiaFonEQar9A8O4hHe67Gk0DmSO-Hm+b4KsJMgSWpGlvL1JOhWyYQYJWZ6ck2d8PxXtpIYSUjHRfCZLwyUaM2BKRS2IEVO05DAcW4XFh1iHF1AIdgWWNNJkoyW6Kkdz6J5NBcHQ6u1hB9ATdwjFZ14rC3KPFoYWPFXIS3eGT7VU9iHOXYnAxyqaypmk8Yx6TMdDJ716oghGPxy6CFmW5R+h274CUIHFxO2PFzApB76I++IAf5YnB41yCCv6mDXx9F6NCq8DvXzFQ5q9Fscy165tvebjuCMA6B7xJxTmnTA58PSu3zjpUelJOiDCnlrNowRyT0EODcC6VUTA6F-qRDeADFSoAkBAVYx8cCn0gUdQq0CJw3ywrTBBZhKg3BpFXay7gb6WAQnockgxKj4ItkQvgID8CqjAb3CBUDIKKxphUKoNR1aNGaLSDkLhsKBjYTYHQCYK5CL1JvHmc0M4Q3FhQYWsBs40LlnQqCHIsInGwRSLQjQ1C0geLYSMlIeE6H8DwkMBiY4iImpgcx5AcASKoTI4qbtggLx6AEcyIQpzMhrgHVkuhH4r1sFuBcpsuqfnXpvAAVlIWgFDsDRJsbnGBiBh7YS6MwpBJIUE618N0Vc9JrL9CpkExIndeY4DSCQIpM1sq8W6n-RgXd2CjN+vjXKRNjoXygsEdwTdaYjHMEcZ+bRrKdH1rojkZImpkgKT9ZG0zBmOhGWMmGwV4ZhURlMghNzhlQHmcjRZB0RAxLzuoIIGyUJbN0fkvZiBzJrl8VYDQ9wF7fQPL9aZ292C+VgAAFSkF8nU4z+yTKKSiiAV50VYpxTyH5hNDrO1WYrK+PRb4oWZByJ+GTDa6GpNVNQxIF79IYKitIpLsX3KCnDUK4VIqEoIQKqAQryWxEpapalxM7F0sqAylCTKH6spflZSMZcmpNVMMGRMhTkUEJIWQj58riB4rmkjTmFrSG3M+UUxVTt-l1IQAwnoRdx5XzYQHcwGCK6UhJJPFCb0+X0EtS6m1mBAqwxCgjCKDrzZ6ljdat12UCZKr+TUweUEfVMP9awpktIyRcNZDfC6uiQhjDNVcghYjVRzLGRCZs9rXnfhbW2hZOalnKpWaqt2zJuiHFQuCwY2CwyBjfrYJkN1zJRgbZcx1Pbeatrub9RNjzxUvKlRu8RfbvkDt+Z6oeytFFq3qCotpCA9BYSaZUIFvgBhMmjcYjU7BtqEAAIJCxFnajU80m3CJ4j+iGAHLGwHdcs2hsi3YOI5duEI1gFxTnQiHQuzQqivqOH41dSKwOGLABBtIv7oNAYeWKlNkrzXge-RRqDgGrFwaHQh2JJRkNOJ4S4jD7iq5PD6Ikm4DQLAEajNGiAwD7wntxR27iIG03R0SDJkBWb+39lzR6gttLR3qqZEYToJItGTxMlOTRiEhgEYsBc4j669Tqbk9u5Gu7aPPNTd2pzsnNOnu04O-NNKR2wJHk08eLTp5CffRUPorxbrl1MNGhOHzgMEoY45VtHz2NBZVYh-OzJNHF3pMGculd9l4cjDffok8fRUmS2qHYnEaPJs8-RkjMUsvsByxeqCBcitNBLqViuHjmraEqBXPxhwmQL1NWu9NSIqz3j5FAADkSwBpTks+RTs1lPecW8thIq2InYAkZttiPW9MhcQDfcbN1-SBBCP6Dx9JugGQ6jXOoeho1LcwCttbp2NuxHRe51rEqVOt3oL9-7J2zvA-Spd4L+WbuEnuw9+Jz2OG3t0C4qcuimRlx+4d9gAA5MAAB3FMckABGk00ugccwdv7R2yeU++DTyaiO8tcfxE8cqIwX1l1Qty-2HCV6OPuAgnJLhfBE+Z6TinVPkC08hqDp54P9uJGhyzxX7Plec7PVS3Lw7keTkCESEOATKoi8MB4yrYnOghBCMGQw0ayn-GY0WMl7blQTIZwtxI7uWye8IN7rTylz1XdNw0+BEXJ6tPQpUDRvTLe1yZJPN35Tg9QF-WHtzLX1cHoy0iIPkGvfCvD-tI3vW5HVUaWPRB8eov7I0LBfxPCmiHBMA2-cNApAyfgLkTXYBucAoQAAWnK4gcfxw7hz7n6hPBjbGcDOSEsNIKxeCj69a8Dx04mpeBJCHGXNwXANeWlAbf9DAwYJeA0LBPoIXtAfkSaMaTrIDFl8vgPqNnJ-mxsQHmFaG0JxmPoENoNyqYPfkMI-mGCzGuGXOSEGAvBYByOfmjCtGJCDIPqAV6jLu4JAVYH6DAaYE-hdDfuZAuH0CzK+q9OgX-oDGDMDFjEDMxOlFfvYmULflAcQd3uZlXLUKYHBBZFGEys0NVPQXFK5BjMwRJFDO5OigpBwYrNhtwUQQ-qQSZEIbUBdDXByOZH4vZsPj+AwejKtLIRtEwWwVtvQIpsoW7B0HUCGivAEIGPocZAIcGOVDoQMMPAmAupIS5AAR5NgcEYoTRvYaVI-BorYC4Sau4VPu0IMNoI0E3quHhCYIEf-qwclPIYlMQL+jgbYqbl4WodAXwU-hoCMNhK9NULCiVoEFkYwTISESwVYYUUoSbjzg+jdGUbwbAUJh0FhN7H4BoMytYIisYUJNkVYbkcER0XYV0WPiYEHIGE0AhEyNYIJqgiYM9N7DfDLn4HOAYE0WYVgW0S0R0REUsV6ise4GsTwkalotseoCSN0AYL4CHA8FuJSKcStJAJEfiPSNoMGIMFyiSCkpUTdM9DsiSLYCzPwmft-qpr-lIfQNcbgROH6I0Bgm8c1JhDoRSCuF4DOBoE8Mcs1J+kQoCe7NQYyL4PdBGp0PekMDLhbnFp0KhCMEvvNiiTMkMvJjyDSbvkJouhguSM1I-H6NyrotGjKnKkUsKS8QgEML0e-CCshHOHNg5j-jGs6n5jqDSfIirEoreprAHEIUfiHP0KZKwkYYenqL2q5oaTcROOsh4CClSNsuCrSHwnBNVM0HhFYNYJ4FSeRjnixjBkaXXrHo3sglhiYNhLWkQb0LEfacXmpr5oKbEDSe6Zsl6WCrsuhDFgin4nWgmMCQ1l1mkLmUCh6ahAWTsuSKNmydws1ATnCUibyZDtruwADnDjJOwa6X1lwYZAguYHOP0B4m4McNSLGEMHoF2TqXyb2WkKzkrirkUbUlia9NoL0NuDLsccyMuGLuyqzA0NuDwo-DycuZDqXiHnni6ZiWsnWfmQYIWc2Rwu1ESD4lULhEcEucYbAJQEQJADyLAAAGJDKoA8w3jYCwD4DU4gIQDCn1BZJAoHGGxcmUhqKwRjo3TlxTpPDhDhBAA */
  id: 'multiCartMachine',
  context: ({ input }: { input: MultiCartMachineInput }) => ({
    cartSummary: {
      status: 'loading' as const,
    },
    cartItems: {
      status: 'loading' as const,
    },
    cartMembers: {
      status: 'loading' as const,
    },
    // Initialize with Redux values
    memberId: input.memberId,
    history: input.history,
    currentRegionCode: input.currentRegionCode,
    sharedCartsFeatureFlag: input.sharedCartsFeatureFlag,
    creatingNewCart: false,
  }),
  // Determine initial state - no always transitions at the top level to avoid recursion
  initial: 'checkingState',
  states: {
    checkingState: {
      always: [
        {
          target: 'sharedCartsFeatureDisabled',
          guard: 'isSharedCartsFeatureDisabled'
        },
        {
          target: 'initializing',
          guard: 'isUserLoggedIn'
        },
        {
          target: 'ready'
        }
      ]
    },
    initializing: {
      initial: 'loadingCartSummary',
      states: {
        loadingCartSummary: {
          invoke: {
            src: 'getMultiCarts',
            onDone: {
              target: 'loadingCartDetails',
              actions: ['assignCartSummary', 'loadActiveCartId']
            },
            onError: {
              target: 'error',
              actions: ['logErrorEvent', 'assignCartSummaryFetchError']
            },
          }
        },
        loadingCartDetails: {
          type: 'parallel',
          onDone: {
            target: 'loaded'
          },
          states: {
            loadingCartItems: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartItems',
                    input: ({ context }) => context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
                    onDone: {
                      target: 'done',
                      actions: 'assignCartItems'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartItemsFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
            loadingCartUsers: {
              initial: 'loading',
              states: {
                loading: {
                  invoke: {
                    src: 'getMultiCartMembers',
                    input: ({ context }) => context.cartSummary.status === 'loaded' && context.cartSummary.data ? Object.keys(context.cartSummary.data) : [],
                    onDone: {
                      target: 'done',
                      actions: 'assignCartMembers'
                    },
                    onError: {
                      target: 'error',
                      actions: 'assignCartMembersFetchError'
                    },
                  }
                },
                done: {
                  type: 'final',
                },
                error: {
                  type: 'final',
                },
              }
            },
          }
        },
        loaded: {
          type: 'final'
        },
        error: {
          type: 'final',
        },
      },
      onDone: {
        target: 'ready',
      }
    },
    ready: {
      on: {
        'successful login': {
          target: 'checkingState',
        },
        'failed login': {
          target: 'ready',
        },
        'invite multi cart users': {
          target: 'inviting',
        },
        'create multi cart': {
          target: 'creatingMultiCart',
        },
        'create and move items to target cart': {
          target: 'creatingAndMovingToMultiCart'
        },
        'add items to active cart': {
          target: 'addingItemsToActiveCart',
          actions: [log(({ event }) => `addingItemsToActiveCart ${JSON.stringify(event)}`)],
        },
        'select and move items to target cart': {
          target: 'movingItemsToTargetCart',
          actions: [log(({ event }) => `** movingItemsToTargetCart ${JSON.stringify(event)}`)],
        },
        'delete multi cart': {
          target: 'deletingMultiCart',
        },
        'update multi cart': {
          target: 'updatingMultiCart',
        },
        'leave multi cart': {
          target: 'leavingMultiCart',
        },
        'revoke user access': {
          target: 'revokingUserAccess',
        },
        'set active cart': {
          target: 'ready',
          actions: [{
            type: 'assignActiveCartId',
            params: ({ event }: { event: SetActiveCart }) => ({ cartId: event.cartId })
          }],
        },
        'join multi cart': {
          target: 'joiningUserToMultiCart',
        },
        'update cart item': {
          target: 'ready',
          actions: 'updateCartItem',
        },
        'delete all items from active cart': {
          target: 'deletingAllItemsFromActiveCart',
        },
      },
    },
    creatingMultiCart: {
      entry: 'assignCreatingNewCart',
      exit: 'unassignCreatingNewCart',
      invoke: {
        src: 'createMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'create multi cart')
          return {
            cartName: event.cartName,
            items: event.items,
          }
        },
        onDone: {
          target: 'fetchingActiveCartItems',
          actions: [
            log(({ event }) => `createMultiCart onDone ${JSON.stringify(event)}`),
            'showCreateCartSuccessSnackbar',
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<App.MultiCartSummary, string> }) => ({ cartId: event.output.cartId }) },
            'assignNewlyCreatedCartSummary']
        },
        onError: {
          target: 'ready',
          actions: 'showCreateCartErrorSnackbar'
        },
      },
    },
    creatingAndMovingToMultiCart: {
      invoke: {
        src: 'createAndMoveItemsToTargetCart',
        input: ({ event, context }) => {
          assertEvent(event, 'create and move items to target cart')
          return {
            sourceCartId: context.activeCartId!,
            cartName: event.cartName,
            itemIdsToMove: event.multiCartItemIdsToMove,
          }
        },
        onDone: {
          target: 'fetchingActiveCartItems',
          actions: ['showCreateCartSuccessSnackbar',
            { type: 'assignActiveCartId', params: ({ event }: { event: DoneActorEvent<App.MultiCartSummary, string> }) => ({ cartId: event.output.cartId }) },
            'assignNewlyCreatedCartSummary']
        },
        onError: {
          target: 'ready',
          actions: 'showCreateCartErrorSnackbar'
        },
      },
    },
    addingItemsToActiveCart: {
      invoke: {
        src: 'addItemsToMultiCart',
        input: ({ context, event }) => {
          assertEvent(event, ['add items to active cart'])
          return {
            cartId: context.activeCartId!,
            items: event.items,
            skipNotification: event.skipNotification,
          }
        },
        onDone: [{
          target: 'ready',
          actions: ['appendItemsToCart'],
          guard: ({ event }) => {
            assertEvent(event, ['xstate.done.actor.0.multiCartMachine.addingItemsToActiveCart'])
            return event.output.skipNotification
          }
        },
        {
          target: 'ready',
          actions: ['showAddItemsToCartSuccessSnackbar', 'appendItemsToCart']
        }],
        onError: {
          target: 'ready',
          actions: 'showAddItemsToCartErrorSnackbar'
        },
      },
    },
    updatingMultiCart: {
      invoke: {
        src: 'updateMultiCartName',
        input: ({ event }) => {
          assertEvent(event, 'update multi cart')
          return {
            cartId: event.cartId,
            name: event.name,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showUpdateCartSuccessSnackbar', 'assignUpdatedCartSummary']
        },
        onError: {
          target: 'ready',
          actions: 'showUpdateCartErrorSnackbar'
        },
      },
    },
    leavingMultiCart: {
      invoke: {
        src: 'leaveMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'leave multi cart')
          return {
            cartId: event.cartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['setDefaultCartAsActive', 'showLeaveCartSuccessSnackbar']
        },
        onError: {
          target: 'ready',
          actions: 'showLeaveCartErrorSnackbar'
        },
      },
    },
    revokingUserAccess: {
      invoke: {
        src: 'revokeUserAccessMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'revoke user access')
          return {
            cartId: event.cartId,
            userId: event.userId,
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showRevokeUserAccessSuccessSnackbar', 'assignRevokedUserAccess']
        },
        onError: {
          target: 'ready',
          actions: 'showRevokeUserAccessErrorSnackbar'
        },
      },
    },
    deletingMultiCart: {
      invoke: {
        src: 'deleteMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'delete multi cart')
          return {
            cartId: event.cartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['setDefaultCartAsActive', 'showDeleteCartSuccessSnackbar']
        },
        onError: {
          target: 'ready',
          actions: 'showDeleteCartErrorSnackbar'
        },
      },
    },
    inviting: {
      invoke: {
        src: 'inviteUsersToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'invite multi cart users')
          return {
            cartId: event.cartId,
            users: event.users,
          }
        },
        onDone: {
          target: 'fetchingNewCartMembers',
          actions: 'showInvitationSuccessSnackbar'
        },
        onError: {
          target: 'ready',
          actions: 'showInvitationErrorSnackbar'
        },
      },
    },
    fetchingActiveCartItems: {
      invoke: {
        src: 'getMultiCartItems',
        input: ({ context }) => [context.activeCartId!],
        onDone: {
          target: 'fetchingNewCartMembers',
          actions: 'appendCartItems'
        },
        onError: {
          target: 'ready',
          actions: 'assignCartItemsFetchError',
        },
      },
    },
    fetchingNewCartMembers: {
      invoke: {
        src: 'getMultiCartMembers',
        input: ({ context }) => [context.activeCartId!],
        onDone: {
          target: 'ready',
          actions: 'appendCartMembers'
        },
        onError: {
          target: 'ready',
          actions: 'assignCartMembersFetchError',
        },
      },
    },
    joiningUserToMultiCart: {
      invoke: {
        src: 'joinUserToMultiCart',
        input: ({ event }) => {
          assertEvent(event, 'join multi cart')
          return {
            sharedCartId: event.sharedCartId,
          }
        },
        onDone: {
          target: 'initializing',
          actions: ['showJoinCartSuccessSnackbar', 'pushToCartPage'],
        },
        onError: {
          target: 'ready',
          actions: 'showJoinCartErrorSnackbar',
        },
      },
    },
    movingItemsToTargetCart: {
      invoke: {
        src: 'moveItemsToTargetCart',
        input: ({ event, context }) => {
          assertEvent(event, 'select and move items to target cart')

          return {
            sourceCartId: context.activeCartId!,
            targetCartId: event.targetCartId,
            itemIdsToMove: event.multiCartItemIdsToMove,
          }
        },
        onDone: {
          target: 'fetchingActiveCartItems',
          actions: [{
            type: 'assignActiveCartId',
            params: ({ event }: { event: DoneActorEvent<{ targetCartId: string }, string> }) => ({ cartId: event.output.targetCartId })
          }, 'showMoveItemsToCartSuccessSnackbar'],
        },
        onError: {
          target: 'ready',
          actions: 'showAddItemsToCartErrorSnackbar'
        },
      },
    },
    deletingAllItemsFromActiveCart: {
      invoke: {
        src: 'deleteItemsInMultiCart',
        input: ({ event, context }) => {
          assertEvent(event, 'delete all items from active cart')
          return {
            cartId: context.activeCartId!,
            itemIdsToDelete: context.cartItems.status === 'loaded' ? Object.keys(context.cartItems.data[context.activeCartId!]) : [],
          }
        },
        onDone: {
          target: 'ready',
          actions: ['showDeleteAllItemsFromCartSuccessSnackbar', 'emptyCartItemsOfActiveCart']
        },
        onError: {
          target: 'ready',
          actions: 'showDeleteAllItemsFromCartErrorSnackbar'
        },
      },
    },
    sharedCartsFeatureDisabled: {
      type: 'final',
    }
  },
})

export default multiCartMachine
